import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Calendar, Clock, MapPin, Users, Star, ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react';

const BootcampDetailModal = ({ bootcamp, open, onClose, onBook }) => {
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);

  if (!bootcamp) return null;

  const allImages = [
    ...(bootcamp.image_url ? [{ url: bootcamp.image_url, caption: bootcamp.name }] : []),
    ...(bootcamp.gallery || [])
  ];

  const nextImage = () => {
    setActiveGalleryIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setActiveGalleryIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    const end = new Date(endDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return `${start} - ${end}`;
  };

  const spotsLeft = bootcamp.capacity - (bootcamp.booked_count || 0);
  const isFull = spotsLeft <= 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-neutral-900 border-neutral-800 overflow-hidden">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Image Gallery */}
        {allImages.length > 0 && (
          <div className="relative h-72 bg-black">
            <img 
              src={allImages[activeGalleryIndex]?.url} 
              alt={allImages[activeGalleryIndex]?.caption || bootcamp.name}
              className="w-full h-full object-cover"
            />
            
            {allImages.length > 1 && (
              <>
                <button 
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                
                {/* Dots indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {allImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveGalleryIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === activeGalleryIndex ? 'bg-white w-4' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
            
            {allImages[activeGalleryIndex]?.caption && (
              <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur px-3 py-1 rounded text-white text-sm">
                {allImages[activeGalleryIndex].caption}
              </div>
            )}
          </div>
        )}
        
        <ScrollArea className="max-h-[calc(90vh-288px)]">
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-white">{bootcamp.name}</DialogTitle>
            </DialogHeader>
            
            {/* Key Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6">
              <div className="bg-neutral-800/50 rounded-lg p-4">
                <Calendar className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-xs text-white/50">Dates</p>
                <p className="text-sm text-white font-medium">
                  {formatDateRange(bootcamp.start_date, bootcamp.end_date)}
                </p>
              </div>
              <div className="bg-neutral-800/50 rounded-lg p-4">
                <Clock className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-xs text-white/50">Time</p>
                <p className="text-sm text-white font-medium">{bootcamp.time || '9:00 AM - 4:00 PM'}</p>
              </div>
              <div className="bg-neutral-800/50 rounded-lg p-4">
                <MapPin className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-xs text-white/50">Location</p>
                <p className="text-sm text-white font-medium">{bootcamp.location}</p>
              </div>
              <div className="bg-neutral-800/50 rounded-lg p-4">
                <Users className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-xs text-white/50">Age Range</p>
                <p className="text-sm text-white font-medium">{bootcamp.age_min}-{bootcamp.age_max} years</p>
              </div>
            </div>
            
            {/* Description */}
            {bootcamp.description && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-white mb-2">About This Bootcamp</h4>
                <p className="text-white/70 whitespace-pre-line">{bootcamp.description}</p>
              </div>
            )}
            
            {/* Features */}
            {bootcamp.features && bootcamp.features.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-white mb-3">What's Included</h4>
                <div className="flex flex-wrap gap-2">
                  {bootcamp.features.map((feature, idx) => (
                    <Badge key={idx} className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Guest Teachers */}
            {bootcamp.guest_teachers && bootcamp.guest_teachers.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-white mb-3">Guest Teachers</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {bootcamp.guest_teachers.map((teacher, idx) => (
                    <div key={idx} className="flex items-start gap-4 bg-neutral-800/30 rounded-lg p-4">
                      {teacher.image_url ? (
                        <img 
                          src={teacher.image_url} 
                          alt={teacher.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/30"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Star className="w-8 h-8 text-purple-400" />
                        </div>
                      )}
                      <div>
                        <h5 className="font-semibold text-white">{teacher.name}</h5>
                        {teacher.role && <p className="text-purple-400 text-sm">{teacher.role}</p>}
                        {teacher.bio && <p className="text-white/60 text-sm mt-1">{teacher.bio}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Additional Info */}
            {bootcamp.additional_info && (
              <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <h4 className="text-amber-400 font-semibold mb-2">Important Information</h4>
                <p className="text-white/70 text-sm whitespace-pre-line">{bootcamp.additional_info}</p>
              </div>
            )}
          </div>
        </ScrollArea>
        
        {/* Footer */}
        <DialogFooter className="p-6 bg-neutral-950 border-t border-neutral-800">
          <div className="flex items-center justify-between w-full">
            <div>
              <span className="text-3xl font-bold text-white">£{bootcamp.price}</span>
              <span className="text-white/50 ml-2">per week</span>
              {!isFull && (
                <p className="text-sm text-green-400 mt-1">{spotsLeft} spots remaining</p>
              )}
            </div>
            <Button 
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8"
              onClick={onBook}
              disabled={isFull}
              data-testid="book-bootcamp-modal-btn"
            >
              <Sparkles className="mr-2 w-5 h-5" />
              {isFull ? 'Fully Booked' : 'Book Now'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BootcampDetailModal;
